@use "@styles/tokens";
@use "@styles/component-mixins" as component;
@use "ButtonFocus" as *;

// Visual source of truth: docs/specs/2026-08-20-button-figma-extraction.md.
// Every colour, spacing and radius below is a registry token; heights and icon
// boxes are literal px (dimensions, not spacing) per the plan's exception.
//
// State precedence is hover -> focus -> pressed, so a keyboard-held button keeps
// its pressed colours while the focus ring stays visible. `:disabled` never
// matches an interactive rule.

@layer base-components {
    // Forced states let the playground render hover/press/focus statically.
    // Contract: data-forced-state="hover" | "pressed" | "focus" (playground-only).
    // A Button that opens a Menu, Picker or Popover keeps its hover look while
    // the popup is open. Tooltip triggers also set `data-popup-open`; only popup
    // triggers pair it with `aria-expanded`.
    @mixin on-hover {
        &:not(:disabled):hover,
        &:not(:disabled)[data-popup-open][aria-expanded="true"],
        &[data-forced-state="hover"] {
            @content;
        }
    }

    @mixin on-pressed {
        &:not(:disabled):active,
        &[data-forced-state="pressed"] {
            @content;
        }
    }

    // One ring for all seven variants (extraction §1). `on-focus` and
    // `$ring-shadow` come from ButtonFocus.scss.
    @mixin focus-ring {
        --button-border: var(--border-accent-focus);

        outline: none;
        box-shadow: $ring-shadow;
    }

    @mixin size($height, $icon, $padding-x, $gap, $icon-only-padding-x) {
        --button-padding-x: #{$padding-x};
        --button-gap: #{$gap};

        height: $height;

        svg {
            width: $icon;
            height: $icon;
        }

        // Icon-only is square: width tracks height, padding-x drops one step.
        &.iconOnly {
            --button-padding-x: #{$icon-only-padding-x};

            width: $height;
        }
    }

    .button {
        --button-bg: transparent;
        --button-fg: var(--fg-primary);
        --button-border: transparent;
        --button-gap: var(--space-8);
        --button-padding-x: var(--space-12);
        --button-radius: var(--radius-m);

        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--button-gap);
        padding: 0 var(--button-padding-x);
        background-color: var(--button-bg);
        color: var(--button-fg);

        // Always reserved, even when transparent: Ghost is borderless at rest but
        // gains a border on hover, press and disable, and must not shift 1px.
        border: var(--border-width) solid var(--button-border);
        border-radius: var(--button-radius);
        text-decoration: none;
        text-wrap: nowrap;
        transition:
            background-color var(--duration-fast) ease,
            border-color var(--duration-fast) ease,
            color var(--duration-fast) ease,
            box-shadow var(--duration-fast) ease;

        svg {
            flex-shrink: 0;
        }

        // The Figma chevron glyph (8px frame, 6×3 stroke) keeps its intrinsic
        // size instead of stretching to the button's icon size.
        svg[viewBox="0 0 8 8"] {
            width: 8px;
            height: 8px;
        }

        @include component.recipe-icon-color;

        @include on-hover {
            cursor: pointer;
        }

        @include on-pressed {
            transition-duration: 0ms;
        }
    }

    // --- Sizes (extraction §2) ------------------------------------------------
    // The text style here is the per-size default; variants override it below.

    // 20px is the only size off --radius-m, and only outside Link.
    .xs {
        --button-radius: var(--radius-s);

        @include tokens.text-xs-12-medium;
        @include size(
            $height: 20px,
            $icon: 12px,
            $padding-x: var(--space-6),
            $gap: var(--space-4),
            $icon-only-padding-x: var(--space-4)
        );
    }

    .sm {
        @include tokens.text-xs-12-medium;
        @include size(
            $height: 24px,
            $icon: 12px,
            $padding-x: var(--space-8),
            $gap: var(--space-4),
            $icon-only-padding-x: var(--space-6)
        );
    }

    .md {
        @include tokens.text-s-14-medium;
        @include size(
            $height: 28px,
            $icon: 16px,
            $padding-x: var(--space-8),
            $gap: var(--space-4),
            $icon-only-padding-x: var(--space-6)
        );
    }

    .lg {
        @include tokens.text-s-14-medium;
        @include size(
            $height: 32px,
            $icon: 16px,
            $padding-x: var(--space-12),
            $gap: var(--space-8),
            $icon-only-padding-x: var(--space-8)
        );
    }

    .xl {
        @include tokens.text-s-14-medium;
        @include size(
            $height: 40px,
            $icon: 16px,
            $padding-x: var(--space-16),
            $gap: var(--space-8),
            $icon-only-padding-x: var(--space-12)
        );
    }

    // --- Variants (extraction §1) --------------------------------------------
    // Declared after the sizes so per-variant text styles win at equal specificity.

    .primary {
        --button-bg: var(--bg-primary);
        --button-fg: var(--fg-primary);
        --button-border: var(--border-default);

        @include on-hover {
            --button-border: var(--border-hover);
        }

        @include on-focus {
            @include focus-ring;
        }

        @include on-pressed {
            --button-bg: var(--bg-secondary);
            --button-fg: var(--fg-secondary);
            --button-border: var(--border-hover);
        }

        &:disabled {
            --button-fg: var(--fg-disabled);
            --button-border: var(--border-disabled);
        }

        // Form-field triggers (Select, Combobox, DatePicker, pickers) share
        // Input's invalid chrome; the focus ring still wins while focused.
        &[aria-invalid="true"]:not(:disabled):not(:focus-visible):not(
                [data-forced-state="focus"]
            ) {
            --button-border: var(--border-error);

            &:hover,
            &[data-popup-open][aria-expanded="true"],
            &[data-forced-state="hover"] {
                --button-border: var(--border-error-hover);
            }
        }
    }

    // The only alpha-ramp variant; no border outside focus.
    .secondary {
        @include on-hover {
            --button-bg: var(--bg-neutral-hover);
        }

        @include on-focus {
            @include focus-ring;

            --button-bg: var(--bg-primary);
        }

        @include on-pressed {
            --button-bg: var(--bg-neutral-pressed);
            --button-fg: var(--fg-secondary);
        }

        &:disabled {
            --button-fg: var(--fg-disabled);
        }
    }

    // Filled accent (despite the name): the solid blue fill ramp.
    .tertiary {
        --button-bg: var(--bg-accent);
        --button-fg: var(--fg-on-solid);

        @include on-hover {
            --button-bg: var(--bg-accent-hover);
        }

        @include on-focus {
            @include focus-ring;

            --button-bg: var(--bg-accent);
        }

        @include on-pressed {
            --button-bg: var(--bg-accent-pressed);
        }

        &:disabled {
            --button-bg: var(--bg-disabled);
            --button-fg: var(--fg-tertiary);
        }
    }

    .destructive {
        --button-bg: var(--bg-primary);
        --button-fg: var(--fg-error);
        --button-border: var(--border-error);

        // Destructive/24 renders S 14 Medium where every other variant at 24 is
        // XS 12 Medium (extraction §3).
        &.sm {
            @include tokens.text-s-14-medium;
        }

        @include on-hover {
            --button-border: var(--border-error-hover);
        }

        // Focus is the shared accent ring, not an error ring.
        @include on-focus {
            @include focus-ring;
        }

        @include on-pressed {
            --button-bg: var(--bg-error-outline-pressed);
            --button-border: var(--border-error-hover);
        }

        &:disabled {
            --button-fg: var(--fg-error-disabled);
            --button-border: var(--border-error-disabled);
        }
    }

    .ghost {
        @include tokens.text-s-14-regular;

        @include on-hover {
            --button-border: var(--border-hover);
        }

        @include on-focus {
            @include focus-ring;

            --button-bg: var(--bg-primary);
        }

        @include on-pressed {
            --button-fg: var(--fg-secondary);
            --button-border: var(--border-hover);
        }

        &:disabled {
            --button-bg: var(--bg-primary);
            --button-fg: var(--fg-disabled);
            --button-border: var(--border-disabled);
        }
    }

    // Ghost's second label style at the same 28px height.
    .ghostXsMedium {
        @include tokens.text-xs-12-medium;
    }

    // Outlined accent. Figma tags the label "Medium" but renders S 14 Regular.
    .accent {
        @include tokens.text-s-14-regular;

        --button-bg: var(--bg-primary);
        --button-fg: var(--fg-accent);
        --button-border: var(--border-accent);

        @include on-hover {
            --button-border: var(--border-accent-hover);
        }

        // Figma authors the focus label as the neutral --fg-primary; design
        // confirmed that is an authoring mistake (feedback 2026-08-20), so the
        // accent label colour is kept through focus. Ring and border unchanged.
        @include on-focus {
            @include focus-ring;
        }

        @include on-pressed {
            --button-bg: var(--bg-accent-outline-pressed);
            --button-fg: var(--fg-accent-pressed);
            --button-border: var(--border-accent-hover);
        }

        &:disabled {
            --button-fg: var(--fg-accent-disabled);
            --button-border: var(--border-accent-disabled);
        }
    }

    // Padding-less text button on the neutral ramp: no underline, no accent
    // colour, in any state.
    .link {
        --button-padding-x: 0;

        // Link/20 keeps --radius-m where every other 20px button is --radius-s,
        // and its 18px line box is centred in a 20px flex row (no padding-block).
        &.xs {
            --button-radius: var(--radius-m);
        }

        &.sm {
            @include tokens.text-s-14-medium;
        }

        @include on-hover {
            --button-fg: var(--fg-secondary);
        }

        @include on-focus {
            @include focus-ring;

            --button-bg: var(--bg-primary);
            --button-fg: var(--fg-primary);
        }

        @include on-pressed {
            --button-fg: var(--fg-secondary);
        }

        &:disabled {
            --button-fg: var(--fg-disabled);
        }
    }

    // --- Slots ----------------------------------------------------------------

    .leftSlot,
    .rightSlot {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
    }

    // Callers mark box content with a bare `<span data-slot-box>` around an
    // inline-flex chip. Left as a block, that span lays the chip on a text line
    // box and drops it 1-2px below the label's center.
    .leftSlot > [data-slot-box],
    .rightSlot > [data-slot-box] {
        display: flex;
    }

    // Squarish slot content — a tag chip, entity icon or shortcut chip — carries
    // more visual weight at the edge than a glyph, so Figma tightens that side's
    // padding by one 4px step: 24px 8->4 (nodes 3333:920 tag, 3333:3204 shortcut),
    // 32px 12->8 (node 3332:431 tag). A plain icon slot is NOT tightened (node
    // 3030:982 stays 12/12), which is why the trim is opted into by the content
    // marking itself `data-slot-box` rather than driven off the icon props. This
    // replaces the older `hasRightSlot` rule, which fired on any right icon and
    // pinned a flat 8px instead of stepping down from the size's own padding.
    .button:not(.iconOnly) {
        &:has(.leftSlot > [data-slot-box]) {
            padding-left: calc(var(--button-padding-x) - var(--space-4));
        }

        &:has(.rightSlot > [data-slot-box]) {
            padding-right: calc(var(--button-padding-x) - var(--space-4));
        }
    }

    // --- Modifiers ------------------------------------------------------------

    // Promote off inline-flex so stacked full-width buttons (menu items, form
    // submits) don't sit on inline line boxes and stretch to the popup max-width.
    .block {
        display: flex;
        width: 100%;
    }
}
